import { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useMemo, useRef } from 'react';
import { SocketStatusChangeEvent, UnionOfMessagePayloads } from '@m43/sockets-mark43';
import { StreamMap } from '../../socket/stream-types';
import { createStreamListener, useStream, useSocketStatus } from '../../socket/use-stream';
import { PromiseBuffer } from '../promises/promise-buffer';

const pnoop = () => Promise.resolve();

type BaseProps<N extends Readonly<(keyof StreamMap)[]>> = {
    streamNames: N;
    query: UseQueryResult<unknown, unknown>;
    enabled?: boolean;
    onReconnect?: (
        socketStatusChangeEvent: SocketStatusChangeEvent<StreamMap, keyof StreamMap>
    ) => Promise<unknown>;
};

type Props<T, N extends Readonly<(keyof StreamMap)[]>> =
    | (BaseProps<N> & {
          onMessage: (message: UnionOfMessagePayloads<StreamMap, N[number]>) => T[];
          promiseBufferCallback: (val: T[]) => Promise<unknown>;
      })
    | (BaseProps<N> & {
          onMessage: (message: UnionOfMessagePayloads<StreamMap, N[number]>) => void;
      });

export function useIntegratedStreams<T, N extends Readonly<(keyof StreamMap)[]>>(
    props: Props<T, N>
) {
    const { streamNames, query, onMessage, enabled, onReconnect } = props;

    const callbackRef = useRef<(val: T[]) => Promise<unknown>>();
    const onReconnectRef = useRef<undefined | BaseProps<any>['onReconnect']>();
    onReconnectRef.current = onReconnect;
    const promiseBufferCallback =
        'promiseBufferCallback' in props ? props.promiseBufferCallback : pnoop;
    callbackRef.current = props.enabled === false ? pnoop : promiseBufferCallback;

    const promiseBuffer = useMemo(() => {
        return new PromiseBuffer<T>({
            delay: 100,
            callback: (inputs) => {
                return callbackRef.current?.(inputs) ?? Promise.resolve();
            },
        });
    }, []);

    const streamListener = useMemo(
        () =>
            createStreamListener({
                streamNames,
                onMessage(message) {
                    if (enabled === false) return;
                    const inputs = onMessage(message);
                    if (inputs) {
                        for (const input of inputs) {
                            promiseBuffer.enqueue(input);
                        }
                    }
                },
            }),
        [streamNames, enabled, onMessage, promiseBuffer]
    );

    useStream(streamListener);

    const refetch = query.refetch;
    const isFetched = query.isFetched;
    const isFetching = query.isFetching;
    const socketStatusListener = useMemo(
        () => ({
            invokeOnInitialize: true,
            onSocketStatusChange(
                socketStatusChangeEvent: SocketStatusChangeEvent<StreamMap, keyof StreamMap>
            ) {
                if (socketStatusChangeEvent.status === 'CONNECTED' && enabled !== false) {
                    if (onReconnectRef.current) {
                        promiseBuffer.stop();
                        promiseBuffer.reset();
                        onReconnectRef.current(socketStatusChangeEvent).then(() => {
                            promiseBuffer.start();
                        });
                    } else {
                        refetch();
                    }
                }
            },
        }),
        [refetch, enabled, promiseBuffer]
    );

    // Refetch the query when the component first mounts (if it has not been fetched)
    // Refetch the query again any time its query keys change
    // (since when the query keys change, `isFetched` gets set back to false)
    // Don't refetch if enabled === false
    useEffect(() => {
        if (!isFetched && !isFetching && enabled !== false) {
            refetch();
        }
    }, [refetch, isFetched, isFetching, enabled]);

    useSocketStatus(socketStatusListener);

    useEffect(() => {
        if (query.status === 'success') promiseBuffer.start();
        if (query.status === 'loading') promiseBuffer.stop();
    }, [query.status, promiseBuffer]);

    return { promiseBuffer };
}
